import { Col, Divider, Form, Input, Row, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { AttachmentInput } from './AttachmentInput'
import { DeviceTypeSelect } from './DeviceTypeSelect'
import { InstallerInput } from './InstallerInput'
import { LocationPicker } from './LocationPicker'
import { CUSTOMER_TYPES, DEVICE_OWNERSHIPS, type SubscriberProfile } from './profileApi'
import { useMapFallback } from './profileForm'
import { PHONE_E164, normalizePhone } from './subscriberApi'

interface SubscriberProfileFieldsProps {
  mode: 'create' | 'edit'
  /** Tahrirlashda — joriy qiymatlar (fayl bor-yo'qligi, niqoblangan WhatsApp) */
  current?: SubscriberProfile | null
}

export function SubscriberProfileFields({ mode, current }: SubscriberProfileFieldsProps) {
  const { t } = useTranslation()
  const create = mode === 'create'
  const fallback = useMapFallback()
  const required = (label: string) =>
    create ? [{ required: true, message: t('customer.profile.required', { field: label }) }] : []

  const labels = {
    firstName: t('customer.profile.firstName'),
    fatherName: t('customer.profile.fatherName'),
    lastName: t('customer.profile.lastName'),
    deviceType: t('customer.profile.deviceType'),
    deviceOwnership: t('customer.profile.deviceOwnership'),
    customerType: t('customer.profile.customerType'),
    whatsapp: t('customer.profile.whatsapp'),
    photo: t('customer.profile.photo'),
    contract: t('customer.profile.contract'),
    installedBy: t('customer.profile.installedBy'),
    location: t('customer.profile.location'),
  }

  return (
    <>
      <Divider orientation="left" plain style={{ marginTop: 0 }}>
        {t('customer.profile.section')}
      </Divider>

      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'firstName']} label={labels.firstName} rules={[...required(labels.firstName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'fatherName']} label={labels.fatherName} rules={[...required(labels.fatherName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'lastName']} label={labels.lastName} rules={[...required(labels.lastName), { max: 100 }]}>
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'customerType']} label={labels.customerType} rules={required(labels.customerType)}>
            <Select
              options={CUSTOMER_TYPES.map((v) => ({ value: v, label: t(`customer.type.${v}`) }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'deviceTypeId']} label={labels.deviceType} rules={required(labels.deviceType)}>
            <DeviceTypeSelect currentName={current?.deviceTypeName} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name={['profile', 'deviceOwnership']} label={labels.deviceOwnership} rules={required(labels.deviceOwnership)}>
            <Select
              options={DEVICE_OWNERSHIPS.map((v) => ({ value: v, label: t(`customer.deviceOwnership.${v}`) }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={['profile', 'whatsapp']}
        label={labels.whatsapp}
        extra={
          !create && current?.whatsapp
            ? t('customer.profile.whatsappKeep', { value: current.whatsapp })
            : t('customer.profile.whatsappHint')
        }
        rules={[
          ...required(labels.whatsapp),
          {
            validator: (_, value: string | undefined) => {
              const clean = normalizePhone(value)
              if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
              return Promise.reject(new Error(t('customer.profile.whatsappFormat')))
            },
          },
        ]}
      >
        <Input autoComplete="off" inputMode="tel" placeholder={!create ? (current?.whatsapp ?? undefined) : undefined} />
      </Form.Item>

      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item name={['profile', 'photo']} label={labels.photo} rules={required(labels.photo)} extra={t('customer.profile.photoHint')}>
            <AttachmentInput kind="PHOTO" existing={!create && Boolean(current?.photoFileId)} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name={['profile', 'contract']} label={labels.contract} rules={required(labels.contract)} extra={t('customer.profile.contractHint')}>
            <AttachmentInput kind="CONTRACT" existing={!create && Boolean(current?.contractFileId)} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={['profile', 'installer']}
        label={labels.installedBy}
        extra={t('customer.profile.installedByHint')}
        rules={required(labels.installedBy)}
      >
        <InstallerInput />
      </Form.Item>

      <Form.Item name={['profile', 'location']} label={labels.location} rules={required(labels.location)}>
        <LocationPicker fallbackCenter={fallback} />
      </Form.Item>
    </>
  )
}
